import { onBeforeUnmount, onMounted } from 'vue';

/**
 * Runs `tick` on an interval for as long as the component is mounted AND the
 * page is on screen — which is how an agent's reply reaches an open panel.
 *
 * Polling, not a subscription, and on purpose. The contact API is the one
 * process a third-party browser can reach, and it has no WebSocket: a held
 * connection would be a second way into it and would keep an instance busy for
 * every panel left open on every publisher's page. Two existing reads asked
 * again every few seconds need nothing new on the server, stay inside the
 * per-contact rate limit (60 a minute) by a wide margin, and cost a visitor
 * nothing once they look away.
 *
 * Three rules keep it honest:
 *
 * - **Never while hidden.** A background tab is not being read. The timer is
 *   stopped outright rather than left running and skipping, so a tab parked for
 *   a day makes no requests at all — and so the session quietly ageing out
 *   there never raises `identityExpired` on a page nobody is looking at.
 * - **At once on return.** Coming back to the tab asks immediately instead of
 *   up to an interval late; that moment is exactly when a reply is expected.
 * - **One at a time.** A slow response is not overtaken by the next tick, so
 *   answers cannot arrive out of order and a struggling API is not piled onto.
 *
 * `tick` owns its own failures: a poll that fails must leave the screen as it
 * was, never replace a conversation with an error about refreshing it.
 */
export const usePolling = (tick: () => Promise<void>, intervalMs: number): void => {
  let timer: number | undefined;
  let running = false;
  let mounted = false;

  const run = async (): Promise<void> => {
    if (running || !mounted || document.visibilityState !== 'visible') {
      return;
    }

    running = true;

    try {
      await tick();
    } catch {
      // See above: a failed refresh changes nothing on screen.
    } finally {
      running = false;
    }
  };

  const start = (): void => {
    if (timer === undefined) {
      timer = window.setInterval(() => void run(), intervalMs);
    }
  };

  const stop = (): void => {
    if (timer !== undefined) {
      window.clearInterval(timer);
      timer = undefined;
    }
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      start();
      void run();
    } else {
      stop();
    }
  };

  onMounted(() => {
    mounted = true;
    document.addEventListener('visibilitychange', onVisibilityChange);

    if (document.visibilityState === 'visible') {
      start();
    }
  });

  onBeforeUnmount(() => {
    mounted = false;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    stop();
  });
};
